import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ChartFrame, ChartTable, LineChart } from '../../components/charts';
import { Input } from '../../components/ui';
import {
  adminApi,
  formatMoney,
  type Granularity,
} from '../../lib/adminApi';
import { usePageTitle } from '../../lib/usePageTitle';
import { AdminPanelState, StatTile } from './AdminLayout';

/** Five years back is as far as the yearly view reaches, so the picker matches. */
function yearOptions(): number[] {
  const thisYear = new Date().getFullYear();
  return Array.from({ length: 5 }, (_, index) => thisYear - index);
}

const SELECT_CLASS =
  'rounded-lg border border-hull-600 bg-hull-950 px-3 py-2 text-sm text-white';

export function AdminRevenue() {
  usePageTitle('Revenue — WaveQA Admin');

  const [granularity, setGranularity] = useState<Granularity>('monthly');
  const [year, setYear] = useState(new Date().getFullYear());
  const [plan, setPlan] = useState('');
  const [start, setStart] = useState('');
  const [end, setEnd] = useState('');

  // A custom range is only a valid query once both ends are set. Sending a half
  // range would answer 422 on every keystroke while the operator fills the form.
  const customReady = granularity !== 'custom' || (start !== '' && end !== '');

  const revenue = useQuery({
    queryKey: ['admin', 'revenue', granularity, year, plan, start, end],
    queryFn: () =>
      adminApi.getRevenue({
        granularity,
        year,
        plan: plan || undefined,
        start: granularity === 'custom' ? start : undefined,
        end: granularity === 'custom' ? end : undefined,
      }),
    enabled: customReady,
  });

  const currency = revenue.data?.currency ?? 'USD';

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-white">Revenue</h1>
        <p className="mt-1 text-sm text-hull-400">
          Payments confirmed by the billing provider.
        </p>
      </div>

      <div className="flex flex-wrap items-end gap-3">
        <div>
          <label htmlFor="rev-period" className="mb-1.5 block text-xs text-hull-400">
            Period
          </label>
          <select
            id="rev-period"
            value={granularity}
            onChange={(event) => setGranularity(event.target.value as Granularity)}
            className={SELECT_CLASS}
          >
            <option value="monthly">Monthly</option>
            <option value="quarterly">Quarterly</option>
            <option value="yearly">Yearly</option>
            <option value="custom">Custom range</option>
          </select>
        </div>

        {granularity !== 'yearly' && granularity !== 'custom' && (
          <div>
            <label htmlFor="rev-year" className="mb-1.5 block text-xs text-hull-400">
              Year
            </label>
            <select
              id="rev-year"
              value={year}
              onChange={(event) => setYear(Number(event.target.value))}
              className={SELECT_CLASS}
            >
              {yearOptions().map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
          </div>
        )}

        {granularity === 'custom' && (
          <>
            <div>
              <label htmlFor="rev-start" className="mb-1.5 block text-xs text-hull-400">
                From
              </label>
              <Input
                id="rev-start"
                type="date"
                value={start}
                onChange={(event) => setStart(event.target.value)}
              />
            </div>
            <div>
              <label htmlFor="rev-end" className="mb-1.5 block text-xs text-hull-400">
                To
              </label>
              <Input
                id="rev-end"
                type="date"
                value={end}
                onChange={(event) => setEnd(event.target.value)}
              />
            </div>
          </>
        )}

        <div>
          <label htmlFor="rev-plan" className="mb-1.5 block text-xs text-hull-400">
            Plan
          </label>
          <select
            id="rev-plan"
            value={plan}
            onChange={(event) => setPlan(event.target.value)}
            className={SELECT_CLASS}
          >
            <option value="">All plans</option>
            <option value="free">Free</option>
            <option value="basic">Basic</option>
            <option value="standard">Standard</option>
            <option value="premium">Premium</option>
          </select>
        </div>
      </div>

      {!customReady && (
        <p className="rounded-card border border-dashed border-hull-700 px-4 py-3 text-sm text-hull-400">
          Choose both a start and an end date to see a custom range.
        </p>
      )}

      {customReady && (
        <AdminPanelState loading={revenue.isPending} error={revenue.error}>
          {revenue.data && (
            <div className="space-y-6">
              {revenue.data.plan_filter_is_approximate && (
                <p
                  role="status"
                  className="rounded-card border border-warn-600/40 bg-warn-600/10 px-4 py-3 text-sm leading-relaxed text-warn-400"
                >
                  A payment records a price, not a plan, so this filter resolves through
                  each customer&rsquo;s <strong>current</strong> subscription. A customer
                  who changed plan has their older payments counted under the new one.
                </p>
              )}

              {revenue.data.excluded_payments > 0 && (
                <p
                  role="status"
                  className="rounded-card border border-warn-600/40 bg-warn-600/10 px-4 py-3 text-sm text-warn-400"
                >
                  {revenue.data.excluded_payments} completed payment
                  {revenue.data.excluded_payments === 1 ? ' is' : 's are'} in another
                  currency and excluded from these totals.
                </p>
              )}

              <ChartFrame
                title={
                  revenue.data.granularity === 'yearly'
                    ? 'Revenue by year'
                    : revenue.data.granularity === 'custom'
                      ? 'Revenue over the selected range'
                      : `Revenue — ${revenue.data.year}`
                }
                caption="Only payments the provider confirmed. Failed and refunded transactions are not counted."
                table={
                  <ChartTable
                    columns={['Period', 'Revenue']}
                    rows={revenue.data.series.map((point) => [
                      point.label,
                      formatMoney(point.value, currency, { exact: true }),
                    ])}
                  />
                }
              >
                <LineChart
                  points={revenue.data.series}
                  format={(value) => formatMoney(value, currency)}
                  emptyNote="No confirmed payments in this period."
                />
              </ChartFrame>

              <section aria-labelledby="rev-stats">
                <h2 id="rev-stats" className="sr-only">
                  Revenue statistics
                </h2>
                <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
                  <StatTile
                    label="Total revenue"
                    value={formatMoney(revenue.data.stats.total_minor_units, currency, {
                      exact: true,
                    })}
                    hint="all time"
                  />
                  <StatTile
                    label="This month"
                    value={formatMoney(revenue.data.stats.this_month_minor_units, currency, {
                      exact: true,
                    })}
                  />
                  <StatTile
                    label="Last month"
                    value={formatMoney(revenue.data.stats.last_month_minor_units, currency, {
                      exact: true,
                    })}
                  />
                  <StatTile
                    label="This year"
                    value={formatMoney(revenue.data.stats.this_year_minor_units, currency, {
                      exact: true,
                    })}
                  />
                  <StatTile
                    label="Average month"
                    value={formatMoney(
                      revenue.data.stats.average_month_minor_units,
                      currency,
                      { exact: true },
                    )}
                    hint="this year, over elapsed months"
                  />
                  <StatTile
                    label="MRR / ARR"
                    value={`${formatMoney(revenue.data.stats.mrr_minor_units, currency)} / ${formatMoney(
                      revenue.data.stats.arr_minor_units,
                      currency,
                    )}`}
                    hint="contracted, not collected"
                  />
                </div>
              </section>

              <section className="rounded-card border border-hull-700 bg-hull-800/60 p-5 sm:p-6">
                <h2 className="text-sm font-semibold uppercase tracking-[0.14em] text-hull-300">
                  Contracted revenue by plan
                </h2>
                <p className="mt-1.5 text-xs leading-relaxed text-hull-400">
                  MRR is what the current book bills per month, derived from
                  subscriptions and the plan catalogue. The chart above is money the
                  provider actually confirmed. The two are meant to differ — the gap is
                  churn, dunning and proration.
                </p>
                <div className="mt-5 overflow-x-auto">
                  <ChartTable
                    columns={['Plan', 'Billing accounts', 'Price', 'Monthly revenue']}
                    rows={revenue.data.plans.map((row) => [
                      row.display_name,
                      row.billing_subscribers,
                      row.monthly_price_minor_units === null
                        ? 'Contact sales'
                        : formatMoney(row.monthly_price_minor_units, currency, { exact: true }),
                      formatMoney(row.monthly_revenue_minor_units, currency, { exact: true }),
                    ])}
                  />
                </div>
              </section>
            </div>
          )}
        </AdminPanelState>
      )}
    </div>
  );
}
